import { getUrl } from "aws-amplify/storage";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Badge from "../../Components/Badge";
import { NumericFormat } from "react-number-format";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../Components/Button";
import { Pagination } from "../../Components/Pagination";
import { addToCart } from "../../Context/checkout/actions";
import { useCheckoutDispatch } from "../../Context/checkout/context";
import {
  requestListEbilling,
  requestListEbillingByBusiness,
} from "../../Context/ebilling/actions";
import {
  useEbillingDispatch,
  useEbillingState,
} from "../../Context/ebilling/context";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import {
  cancelAPIRequest,
  convertToGoogleAnalyticsItem,
  isLastTokenNull,
  trackAddToCart,
} from "../../Utils";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { DateFilter, filterOptions } from "../../Components/DateFilter";
import TableSkeleton from "../../Components/TableSkeleton";
import {
  requestListBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import ErrorSelect from "../../Components/ErrorSelect";
import MaintenanceModal from "../../Components/MaintenanceModal";
import { useRemoteConfig } from "../../Context/firebase/context";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Ebilling = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutDispatch = useCheckoutDispatch();
  const { user } = useAuthState();

  const [page, setpage] = useState(1);
  const [selectedEbilling, setselectedEbilling] = useState({});
  const [selectAll, setselectAll] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[4]);
  const [selectedMonth, setSelectedMonth] = useState({});
  const [selectedYear, setSelectedYear] = useState({});
  const [dateFilter, setDateFilter] = useState({});
  const [selectedBusiness, setSelectedBusiness] = useState({
    value: "ALL",
    label: "All",
  });
  const [allSelectedEbillings, setallSelectedEbillings] = useState([]);

  const dispatch = useEbillingDispatch();
  const { request, ebillings, nextTokens, loading, error } = useEbillingState();

  const businessDispatch = useBusinessTransactionDispatch();
  const {
    businesses,
    loading: businessLoading,
    error: businessError,
  } = useBusinessTransactionState();

  const { ebilling_payment: ebillingPaymentConfig, loading: configLoading } =
    useRemoteConfig();

  const [ebillingPaymentEnabled, setebillingPaymentEnabled] = useState(true);
  const [ebillingPaymentStatus, setebillingPaymentStatus] = useState("");
  const [
    ebillingPaymentMaintenanceVisible,
    setebillingPaymentMaintenanceVisible,
  ] = useState(false);

  const filterBusinessOptions = useMemo(() => {
    return [{ value: "ALL", label: "All" }, ...businesses];
  }, [businesses]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessDispatch, { owner: user.id });
    }
  }, [businessDispatch, user.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const businessID = params.get("business");

    if (
      selectedBusiness !== businessID &&
      businesses.map((business) => business.id).includes(businessID)
    ) {
      const selectedEbillingBusiness = businesses.find(
        (business) => business.id === businessID
      );
      setSelectedBusiness(selectedEbillingBusiness);
    }
  }, [location.search, selectedBusiness, navigate, businesses]);

  useEffect(() => {
    if (user?.id) {
      let createdAt = {};

      if (selectedFilter.value === "TODAY") {
        const startOfDay = dayjs().startOf("day").toISOString();
        const endOfDay = dayjs().endOf("day").toISOString();
        createdAt = {
          between: [startOfDay, endOfDay],
        };
      } else if (selectedFilter.value === "LAST_HOUR") {
        const startOfLastHour = dayjs().subtract(1, "hour").toISOString();
        const endOfNow = dayjs().toISOString();
        createdAt = {
          between: [startOfLastHour, endOfNow],
        };
      } else if (selectedFilter.value === "YESTERDAY") {
        const startOfYesterday = dayjs()
          .subtract(1, "day")
          .startOf("day")
          .toISOString();
        const endOfYesterday = dayjs()
          .subtract(1, "day")
          .endOf("day")
          .toISOString();
        createdAt = {
          between: [startOfYesterday, endOfYesterday],
        };
      } else if (selectedFilter.value === "WEEK") {
        createdAt = {
          gt: dayjs().subtract(7, "day").toISOString().substring(0, 10),
        };
      } else if (selectedFilter.value === "MONTH") {
        createdAt = {
          gt: dayjs().subtract(1, "month").toISOString().substring(0, 10),
        };
      } else if (selectedFilter.value === "OTHER") {
        if (selectedMonth.value && selectedYear.value) {
          const actualMonthValue = parseInt(selectedMonth.value, 10);
          const firstDayOfMonth = dayjs()
            .year(selectedYear.value)
            .month(actualMonthValue - 1)
            .startOf("month")
            .toISOString();
          const lastDayOfMonth = dayjs()
            .year(selectedYear.value)
            .month(actualMonthValue - 1)
            .endOf("month")
            .toISOString();

          createdAt = {
            between: [firstDayOfMonth, lastDayOfMonth],
          };
        }
      }

      setDateFilter(createdAt);

      onLoad(createdAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    selectedBusiness?.value,
    user?.id,
    selectedFilter,
    selectedMonth,
    selectedYear,
  ]);

  useEffect(() => {
    if (ebillings.length > 0) {
      const selectableEbilling = ebillings.filter(
        (ebilling) => ebilling.billingId && ebilling.status === "PENDING"
      );
      if (selectableEbilling.length > 0) {
        const allSelected = selectableEbilling.every(
          (ebilling) => selectedEbilling[ebilling.id]
        );

        setselectAll(allSelected);
      }
    } else {
      setselectAll(false);
    }
  }, [selectedEbilling, ebillings]);

  const handleSelectAll = (checked) => {
    const newSelectedEbilling = {};

    ebillings.forEach((ebilling) => {
      if (
        !ebilling.billingId ||
        ebilling.transactionID ||
        ebilling.status !== "PENDING"
      ) {
        newSelectedEbilling[ebilling.id] = false;
      } else {
        newSelectedEbilling[ebilling.id] = checked;
      }
    });

    setselectedEbilling((previous) => ({
      ...previous,
      ...newSelectedEbilling,
    }));
    setselectAll(checked);
  };

  useEffect(() => {
    const newSelectedEbillings = ebillings.filter(
      (ebilling) => selectedEbilling[ebilling.id] === true
    );

    setallSelectedEbillings((prevAllSelectedEbillings) => {
      const updatedEbillings = [
        ...prevAllSelectedEbillings.filter(
          (prev) => selectedEbilling[prev.id] === true
        ),
        ...newSelectedEbillings.filter(
          (e) => !prevAllSelectedEbillings.some((prev) => prev.id === e.id)
        ),
      ];
      return updatedEbillings;
    });
  }, [selectedEbilling, ebillings]);

  const onLoad = (dateFilter) => {
    if (request) {
      cancelAPIRequest(request);
    }
    setpage(1);
    if (selectedBusiness.value === "ALL") {
      requestListEbilling(dispatch, {
        page: 1,
        limit: 10,
        createdAt: dateFilter,
        owner: user.id,
      });
    } else {
      requestListEbillingByBusiness(dispatch, {
        page: 1,
        limit: 10,
        createdAt: dateFilter,
        businessID: selectedBusiness.value,
      });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      if (selectedBusiness.value === "ALL") {
        requestListEbilling(dispatch, {
          page: nextPage,
          limit: 10,
          createdAt: dateFilter,
          owner: user.id,
          nextToken: nextTokens[nextPage],
        });
      } else {
        requestListEbillingByBusiness(dispatch, {
          page: nextPage,
          limit: 10,
          createdAt: dateFilter,
          businessID: selectedBusiness.value,
          nextToken: nextTokens[nextPage],
        });
      }
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      if (selectedBusiness.value === "ALL") {
        requestListEbilling(dispatch, {
          page: prevPage,
          limit: 10,
          createdAt: dateFilter,
          owner: user.id,
          nextToken: nextTokens[prevPage],
        });
      } else {
        requestListEbillingByBusiness(dispatch, {
          page: prevPage,
          limit: 10,
          createdAt: dateFilter,
          businessID: selectedBusiness.value,
          nextToken: nextTokens[prevPage],
        });
      }
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      if (selectedBusiness.value === "ALL") {
        requestListEbilling(dispatch, {
          page: selectedPage,
          limit: 10,
          createdAt: dateFilter,
          owner: user.id,
          nextToken: nextTokens[selectedPage],
        });
      } else {
        requestListEbillingByBusiness(dispatch, {
          page: selectedPage,
          limit: 10,
          createdAt: dateFilter,
          businessID: selectedBusiness.value,
          nextToken: nextTokens[selectedPage],
        });
      }
    }
  };

  const onPay = () => {
    if (!ebillingPaymentEnabled) {
      setebillingPaymentMaintenanceVisible(true);
    } else {
      const checkoutEbillings = [];
      allSelectedEbillings.forEach((ebilling) => {
        let name = `Pembayaran E-Billing ${ebilling.billingId}`;
        if (ebilling.kap?.name && ebilling.kjs?.name) {
          name = `${ebilling.kap.name} ${ebilling.kjs.name}`;
        }
        let category = ebilling.billingId;
        if (ebilling.startPeriod && ebilling.endPeriod && ebilling.year) {
          category = `${ebilling.startPeriod}${
            ebilling.startPeriod === ebilling.endPeriod
              ? ""
              : `-${ebilling.endPeriod}`
          }/${ebilling.year}`;
        }
        checkoutEbillings.push({
          id: ebilling.id,
          type: "ebilling",
          name,
          detail: {
            category,
            description: ebilling.summary?.npwp,
          },
          price: ebilling.total,
          quantity: 1,
        });
      });

      const analyticsParams = {
        currency: "IDR",
        value: checkoutEbillings.reduce((total, product) => {
          return total + product.price * product.quantity;
        }, 0),
        items: checkoutEbillings.map((product) =>
          convertToGoogleAnalyticsItem(product)
        ),
      };
      logEvent(analytics, "add_to_cart", analyticsParams);
      trackAddToCart({ products: checkoutEbillings });

      addToCart(checkoutDispatch, {
        products: checkoutEbillings,
      });

      navigate("/checkout");
    }
  };

  const onCreate = () => {
    navigate("/ebilling/template");
  };

  const downloadFile = async (key) => {
    const urlResult = await getUrl({
      path: ({ identityId }) => `private/${identityId}/${key}`,
      options: {
        useAccelerateEndpoint: true,
      },
    });
    const signedURL = urlResult.url.href;
    window.open(signedURL);
    try {
    } catch (error) {}
  };

  let totalSelected = 0;
  const selectedEbillingId = Object.keys(selectedEbilling);
  if (selectedEbillingId.length > 0) {
    selectedEbillingId.forEach((ebillingId) => {
      if (selectedEbilling[ebillingId] === true) totalSelected += 1;
    });
  }

  const renderMaintenanceButton = () => {
    const isUnavailable =
      ebillingPaymentStatus === "OFFLINE" ||
      ebillingPaymentStatus === "SCHEDULED_OFFLINE" ||
      ebillingPaymentStatus === "SCHEDULED_ACTIVE";

    const buttonValue = isUnavailable ? "unavailable" : "maintenance";
    const buttonColor = "yellow";

    return (
      <div className="flex items-center">
        <Button
          color={buttonColor}
          type="button"
          onClick={onPay}
          disabled={totalSelected === 0}
          loading={configLoading}
          title={buttonValue}
        />
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col flex-wrap sm:flex-row sm:items-center sm:w-full justify-between gap-4 mb-4">
        <div className="flex flex-col flex-wrap sm:flex-row gap-4">
          <MaintenanceModal
            isVisible={ebillingPaymentMaintenanceVisible}
            onClose={() => setebillingPaymentMaintenanceVisible(false)}
            onChangeAvailability={(available) =>
              setebillingPaymentEnabled(available)
            }
            onChangeStatus={(status) => setebillingPaymentStatus(status)}
            configValue={ebillingPaymentConfig}
          />
          {businessError ? (
            <ErrorSelect
              name="tax"
              customClassNames="mt-0"
              error={businessError}
              onRefresh={() => {
                requestListBusiness(businessDispatch, {
                  owner: user.id,
                });
              }}
            />
          ) : (
            <div>
              <Listbox
                value={selectedBusiness}
                onChange={(business) => {
                  setSelectedBusiness(business);
                  navigate(`/ebilling?business=${business.value}`);
                }}
              >
                {({ open }) => (
                  <>
                    <ListboxButton
                      className={classNames(
                        businessLoading && businesses.length === 0
                          ? "bg-gray-200 border-transparent"
                          : "bg-white",
                        "flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                      )}
                    >
                      <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                        {t(selectedBusiness?.label) || t("Business Name")}
                      </span>
                      {businessLoading && businesses.length === 0 ? (
                        <svg
                          role="status"
                          className="w-5 h-5 animate-spin text-white"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#E5E7EB"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentColor"
                          />
                        </svg>
                      ) : (
                        <SelectorIcon className="w-5 h-5 text-gray-500" />
                      )}
                    </ListboxButton>
                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-auto">
                        {filterBusinessOptions.map((business) => (
                          <ListboxOption
                            key={business.value}
                            className={({ focus }) =>
                              classNames(
                                focus
                                  ? "text-white bg-blue-600"
                                  : "text-gray-900",
                                "cursor-default select-none relative py-2 pl-3 pr-9"
                              )
                            }
                            value={business}
                          >
                            {({ selected, focus }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span
                                    className={classNames(
                                      selected
                                        ? "font-semibold"
                                        : "font-normal",
                                      "ml-3 mr-2 block truncate"
                                    )}
                                  >
                                    {t(business.label)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        focus ? "text-white" : "text-blue-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
          )}
          <DateFilter
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        </div>
        <div className="gap-4 flex justify-start sm:justify-end">
          {ebillingPaymentEnabled ? (
            <Button
              type="button"
              color="green"
              onClick={onPay}
              disabled={totalSelected === 0}
              loading={configLoading}
              title={
                allSelectedEbillings.length > 0
                  ? t("payEbilling", {
                      ebillingTotal: allSelectedEbillings?.length,
                    })
                  : t("Pay")
              }
            />
          ) : (
            renderMaintenanceButton()
          )}
          <Button
            data-testid="create-ebilling-button"
            type="button"
            onClick={onCreate}
            title={t("Create E-Billing")}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="sticky left-0 px-4 py-2 text-left bg-gray-50">
                      <label className="sr-only" htmlFor="row_all">
                        Select All
                      </label>
                      <input
                        className="w-5 h-5 border-gray-200 disabled:bg-gray-100 rounded focus:ring-0 focus:ring-offset-0"
                        type="checkbox"
                        id="row_all"
                        disabled={
                          ebillings.length === 0 ||
                          ebillings.every(
                            (ebilling) =>
                              !ebilling.billingId ||
                              ebilling.transactionID ||
                              ebilling.status !== "PENDING"
                          )
                        }
                        value={selectAll}
                        checked={selectAll}
                        onChange={(event) =>
                          handleSelectAll(event.target.checked)
                        }
                      />
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("ID")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("KAP")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("KJS")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Period")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Total")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("NPWP")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("billingCode")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Payment")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Tax Receipt")}
                    </th>
                  </tr>
                </thead>

                {loading ? (
                  <TableSkeleton rows={3} columns={11} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {ebillings.length > 0 ? (
                      ebillings.map((ebilling) => {
                        const shortId = ebilling.id.substr(
                          ebilling.id.length - 5
                        );
                        return (
                          <tr key={ebilling.id}>
                            <td className="sticky left-0 px-4 py-2 bg-white">
                              <label className="sr-only" htmlFor="row_1">
                                {ebilling.id}
                              </label>
                              <input
                                disabled={
                                  !ebilling.billingId ||
                                  ebilling.transactionID ||
                                  ebilling.status !== "PENDING"
                                }
                                className="w-5 h-5 border-gray-200 disabled:bg-gray-100 rounded focus:ring-0 focus:ring-offset-0"
                                type="checkbox"
                                name={ebilling.id}
                                checked={selectedEbilling[ebilling.id] || false}
                                value={selectedEbilling[ebilling.id] || false}
                                onChange={(event) =>
                                  setselectedEbilling({
                                    ...selectedEbilling,
                                    [event.target.name]: event.target.checked,
                                  })
                                }
                                id={ebilling.id}
                              />
                            </td>
                            <td className="px-4 py-2 font-medium text-sm text-blue-600 underline whitespace-nowrap uppercase">
                              <a href={`/ebilling/${ebilling.id}`}>{shortId}</a>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {ebilling.kap?.id || "-"}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {ebilling.kjs?.kjsId || "-"}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {ebilling.year
                                ? `${ebilling.startPeriod}${
                                    ebilling.endPeriod !== ebilling.startPeriod
                                      ? ` - ${ebilling.endPeriod}`
                                      : ""
                                  } / ${ebilling.year}`
                                : "-"}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {ebilling.total ? (
                                <NumericFormat
                                  displayType={"text"}
                                  value={ebilling.total}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              ) : (
                                `${
                                  ebilling.status === "PENDING_VERIFICATION"
                                    ? t("pendingVerificationLabel")
                                    : t("Failed")
                                }`
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                              {ebilling.summary?.npwp ? (
                                <NumericFormat
                                  value={ebilling.summary.npwp}
                                  displayType={"text"}
                                />
                              ) : (
                                `${
                                  ebilling.status === "PENDING_VERIFICATION"
                                    ? t("pendingVerificationLabel")
                                    : t("Failed")
                                }`
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {ebilling.billingId ? (
                                <span className="text-gray-700">
                                  {ebilling.billingId}
                                </span>
                              ) : (
                                <span className="text-gray-400">
                                  {ebilling.status === "PENDING"
                                    ? t("On Process")
                                    : t("Not Available")}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {ebilling.status === "PENDING_VERIFICATION" ? (
                                <Badge
                                  title="pendingVerificationLabel"
                                  type="alert"
                                />
                              ) : null}
                              {ebilling.status === "PENDING" ? (
                                <Badge title="Pending" type="alert" />
                              ) : null}
                              {ebilling.status === "SUCCESS" ? (
                                <Badge title="Success" type="success" />
                              ) : null}
                              {ebilling.status === "FAILED" ? (
                                <Badge title="Failed" type="danger" />
                              ) : null}
                              {ebilling.status === "EXPIRED" ? (
                                <Badge title="Expired" type="secondary" />
                              ) : null}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {ebilling.billingId ? (
                                <span className="text-gray-700">
                                  {ebilling.transactionID ? (
                                    <a
                                      href={`/transaction/${ebilling.transactionID}`}
                                    >
                                      <span className="text-blue-600 underline text-sm uppercase">
                                        {ebilling.transactionID.substr(
                                          ebilling.transactionID.length - 5
                                        )}
                                      </span>
                                    </a>
                                  ) : ebilling.status ===
                                    "PENDING_VERIFICATION" ? (
                                    t("pendingVerificationLabel")
                                  ) : (
                                    t("Unpaid")
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-400">
                                  {t("Not Available")}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm whitespace-nowrap">
                              {ebilling.output?.bpn?.key ? (
                                <button
                                  onClick={() =>
                                    downloadFile(ebilling.output.bpn.key)
                                  }
                                >
                                  <span className="text-blue-600 underline">
                                    {t("Download")}
                                  </span>
                                </button>
                              ) : (
                                <span className="text-gray-400">
                                  {ebilling.status === "SUCCESS"
                                    ? t("On Process")
                                    : t("Not Available")}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <ErrorStateTable
                        colSpan={12}
                        error={error}
                        emptyTitle={
                          isLastTokenNull(nextTokens)
                            ? "No E-Billing"
                            : "Please click Next again to load the data"
                        }
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ebilling;
